import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import SendIcon from "@mui/icons-material/Send";
import PageLayout from "../../components/PageLayout";
import Spinner from "../../components/Spinner";
import { showToast } from "../../store/slices/common";
import {
  getOfficialDirectMessages,
  getOfficialInbox,
  markOfficialThreadRead,
  replyOfficialDirectMessage,
  searchOfficialMessageUsers,
  sendOfficialSelected,
} from "../../http/adminMessages";

const pollMs = 15000;

function threadTime(value) {
  if (!value) return "";
  return dayjs(value).format("MMM D, h:mm A");
}

function userLabel(user) {
  if (!user) return "";
  return user.username ? `@${user.username}` : user.userId || "";
}

const ManageMessages = () => {
  const dispatch = useDispatch();
  const [loadingInbox, setLoadingInbox] = useState(true);
  const [threads, setThreads] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeContent, setComposeContent] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [composeSending, setComposeSending] = useState(false);
  const messagesEndRef = useRef(null);
  const searchTimer = useRef(null);

  const selectedThread = threads.find(
    (thread) => thread.conversationId === selectedId
  );

  const loadInbox = useCallback(async ({ silent } = {}) => {
    if (!silent) setLoadingInbox(true);
    try {
      const response = await getOfficialInbox();
      setThreads(response?.data?.threads || []);
    } catch (error) {
      dispatch(
        showToast({
          color: "error",
          msg:
            error?.response?.data?.message ||
            "Failed to load @pagent inbox",
        })
      );
    } finally {
      if (!silent) setLoadingInbox(false);
    }
  }, [dispatch]);

  const loadThread = useCallback(
    async (conversationId, { silent } = {}) => {
      if (!conversationId) return;
      if (!silent) setLoadingThread(true);
      try {
        const response = await getOfficialDirectMessages(conversationId);
        setMessages(response?.data?.messages || []);
        await markOfficialThreadRead(conversationId).catch(() => {});
        setThreads((prev) =>
          prev.map((thread) =>
            thread.conversationId === conversationId
              ? { ...thread, isUnread: false }
              : thread
          )
        );
      } catch (error) {
        dispatch(
          showToast({
            color: "error",
            msg:
              error?.response?.data?.message ||
              "Failed to load conversation",
          })
        );
      } finally {
        if (!silent) setLoadingThread(false);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    loadInbox();
  }, [loadInbox]);

  useEffect(() => {
    if (!selectedId) return undefined;
    loadThread(selectedId);
    const timer = setInterval(() => {
      loadThread(selectedId, { silent: true });
      loadInbox({ silent: true });
    }, pollMs);
    return () => clearInterval(timer);
  }, [selectedId, loadThread, loadInbox]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSelectThread = (conversationId) => {
    setSelectedId(conversationId);
    setReply("");
  };

  const handleSendReply = async () => {
    const content = reply.trim();
    if (!content || !selectedId || sending) return;
    setSending(true);
    try {
      await replyOfficialDirectMessage(selectedId, { content });
      setReply("");
      await loadThread(selectedId, { silent: true });
      await loadInbox({ silent: true });
    } catch (error) {
      dispatch(
        showToast({
          color: "error",
          msg:
            error?.response?.data?.message ||
            "Failed to send reply as @pagent",
        })
      );
    } finally {
      setSending(false);
    }
  };

  const searchUsers = (value) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      setSearchingUsers(true);
      try {
        const response = await searchOfficialMessageUsers(value || "");
        setUserOptions(response?.data?.users || []);
      } catch (error) {
        setUserOptions([]);
      } finally {
        setSearchingUsers(false);
      }
    }, 250);
  };

  const openCompose = () => {
    setComposeOpen(true);
    setComposeContent("");
    setSelectedUsers([]);
    searchUsers("");
  };

  const handleComposeSend = async () => {
    const content = composeContent.trim();
    if (!content || selectedUsers.length === 0 || composeSending) return;
    setComposeSending(true);
    try {
      const response = await sendOfficialSelected({
        userIds: selectedUsers.map((user) => String(user.userId)),
        content,
      });
      const result = response?.data || {};
      const sentCount = result.sent?.length || 0;
      const skippedCount = result.skipped?.length || 0;
      const failedCount = result.failed?.length || 0;
      const reasons = [
        ...new Set(
          [...(result.failed || []), ...(result.skipped || [])]
            .map((entry) => entry.reason)
            .filter(Boolean)
        ),
      ];
      const summary = `Sent ${sentCount}. Skipped ${skippedCount}. Failed ${failedCount}.`;
      dispatch(
        showToast({
          color:
            failedCount > 0 ? "error" : skippedCount > 0 ? "warning" : "success",
          msg: reasons.length ? `${summary} ${reasons.join(" ")}` : summary,
        })
      );
      // Keep the draft when nothing went out so the admin can retry.
      if (sentCount > 0) setComposeOpen(false);
      await loadInbox({ silent: true });
      if (sentCount === 1 && result.sent[0]?.conversationId) {
        handleSelectThread(result.sent[0].conversationId);
      }
    } catch (error) {
      dispatch(
        showToast({
          color: "error",
          msg:
            error?.response?.data?.message ||
            "Failed to send messages as @pagent",
        })
      );
    } finally {
      setComposeSending(false);
    }
  };

  return (
    <PageLayout>
      <Box sx={{ height: "90vh", display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              @pagent Messages
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Read and reply as the official account. Users must follow @pagent
              to receive messages.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={openCompose}
          >
            Send to selected users
          </Button>
        </Box>

        {loadingInbox ? (
          <Spinner />
        ) : (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "320px 1fr" },
              gap: 2,
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              overflow: "hidden",
              backgroundColor: "#fff",
            }}
          >
            <Box
              sx={{
                borderRight: { md: "1px solid #e0e0e0" },
                overflowY: "auto",
                minHeight: 280,
              }}
            >
              {threads.length === 0 ? (
                <Box sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
                  <MailOutlineIcon sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
                  <Typography variant="body2">
                    No conversations yet. Users who follow @pagent can message
                    in, or send to selected users.
                  </Typography>
                </Box>
              ) : (
                <List disablePadding>
                  {threads.map((thread) => (
                    <ListItemButton
                      key={thread.conversationId}
                      selected={thread.conversationId === selectedId}
                      onClick={() => handleSelectThread(thread.conversationId)}
                      alignItems="flex-start"
                    >
                      <Avatar
                        src={thread.avatar || undefined}
                        sx={{ width: 40, height: 40, mr: 1.5, mt: 0.5 }}
                      >
                        {(thread.title || "?").slice(0, 1).toUpperCase()}
                      </Avatar>
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: thread.isUnread ? 700 : 500,
                              }}
                            >
                              @{thread.title || "user"}
                            </Typography>
                            {thread.isUnread ? (
                              <Chip size="small" label="New" color="primary" />
                            ) : null}
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              noWrap
                            >
                              {thread.preview || " "}
                            </Typography>
                            <Typography variant="caption" color="text.disabled">
                              {threadTime(thread.lastMessageAt)}
                            </Typography>
                          </>
                        }
                      />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                minHeight: 280,
                minWidth: 0,
              }}
            >
              {!selectedId ? (
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "text.secondary",
                    p: 3,
                  }}
                >
                  Select a conversation to read and reply as @pagent.
                </Box>
              ) : (
                <>
                  <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e0e0e0" }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      @{selectedThread?.title || "user"}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      overflowY: "auto",
                      p: 2,
                      backgroundColor: "#fafafa",
                    }}
                  >
                    {loadingThread ? (
                      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                        <CircularProgress size={28} />
                      </Box>
                    ) : (
                      messages.map((message) => {
                        const own = message.isOwnMessage === true;
                        return (
                          <Box
                            key={message._id}
                            sx={{
                              display: "flex",
                              justifyContent: own ? "flex-end" : "flex-start",
                              mb: 1.25,
                            }}
                          >
                            <Box
                              sx={{
                                maxWidth: "75%",
                                px: 1.5,
                                py: 1,
                                borderRadius: 2,
                                backgroundColor: own ? "#1976d2" : "#fff",
                                color: own ? "#fff" : "inherit",
                                border: own ? "none" : "1px solid #e0e0e0",
                              }}
                            >
                              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                                {message.content}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  display: "block",
                                  mt: 0.5,
                                  opacity: 0.75,
                                }}
                              >
                                {own ? "@pagent · " : ""}
                                {threadTime(message.createdAt)}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })
                    )}
                    <div ref={messagesEndRef} />
                  </Box>
                  <Divider />
                  <Box sx={{ p: 2, display: "flex", gap: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      maxRows={4}
                      placeholder="Reply as @pagent…"
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleSendReply();
                        }
                      }}
                      inputProps={{ maxLength: 1000 }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleSendReply}
                      disabled={!reply.trim() || sending}
                    >
                      {sending ? <CircularProgress size={20} color="inherit" /> : "Send"}
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          </Box>
        )}
      </Box>

      <Dialog
        open={composeOpen}
        onClose={() => !composeSending && setComposeOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Send as @pagent to selected users</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Each selected user gets an individual DM in their Messages inbox.
            Users who unfollowed @pagent are skipped.
          </Typography>
          <Autocomplete
            multiple
            options={userOptions}
            value={selectedUsers}
            loading={searchingUsers}
            filterOptions={(options) => options}
            getOptionLabel={(option) => userLabel(option)}
            isOptionEqualToValue={(option, value) =>
              String(option.userId) === String(value.userId)
            }
            onChange={(_, value) => setSelectedUsers(value)}
            onInputChange={(_, value, reason) => {
              if (reason === "input" || reason === "clear") {
                searchUsers(value);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search users"
                placeholder="Type a username"
                margin="dense"
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.userId}>
                <Avatar
                  src={option.profilePicture || undefined}
                  sx={{ width: 28, height: 28, mr: 1 }}
                >
                  {(option.username || "?").slice(0, 1).toUpperCase()}
                </Avatar>
                {userLabel(option)}
              </li>
            )}
          />
          <TextField
            fullWidth
            multiline
            minRows={4}
            margin="normal"
            label="Message"
            value={composeContent}
            onChange={(e) => setComposeContent(e.target.value)}
            inputProps={{ maxLength: 1000 }}
            helperText={`${composeContent.length}/1000`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setComposeOpen(false)} disabled={composeSending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleComposeSend}
            disabled={
              composeSending ||
              !composeContent.trim() ||
              selectedUsers.length === 0
            }
          >
            {composeSending ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              `Send to ${selectedUsers.length || 0}`
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
};

export default ManageMessages;
